import { Suspense, lazy, useEffect, useState } from 'react';
import styles from '../styles/App.module.css';
import { setShopSlug as registerShopSlug } from '../services/api';
import { resolveDomainBranding } from '../config/domainBranding';

// Cada pantalla se baja recién cuando se entra a ella: así la reserva pública
// no carga el panel, el admin ni la landing (y viceversa).
const BookingForm = lazy(() => import('./BookingForm'));
const LandingPage = lazy(() => import('./LandingPage'));
const SubscriptionAdmin = lazy(() => import('./SubscriptionAdmin'));
const MonitoringPanel = lazy(() => import('./MonitoringPanel'));
const SubscriptionCouponsPage = lazy(() => import('./SubscriptionCouponsPage'));
const SubscriptionCheckoutPage = lazy(() => import('./SubscriptionCheckoutPage'));
const AddBranchPage = lazy(() => import('./AddBranchPage'));
const NotFoundPage = lazy(() => import('./NotFoundPage'));
const PrivacyPolicyPage = lazy(() => import('./PrivacyPolicyPage'));
const AccountDeletionPage = lazy(() => import('./AccountDeletionPage'));
const RegisterAccountPage = lazy(() => import('./RegisterAccountPage'));
const SupportPage = lazy(() => import('./SupportPage'));
const TutorialesPage = lazy(() => import('./TutorialesPage'));
const AlianzaPage = lazy(() => import('./AlianzaPage'));
const AcademyPublic = lazy(() => import('./AcademyPublic'));
const AcademyStudent = lazy(() => import('./AcademyStudent'));
const RichBarbershopLanding = lazy(() => import('./RichBarbershopLanding'));
const SiteUnavailable = lazy(() => import('./SiteUnavailable'));
const PanelApp = lazy(() => import('../panel/PanelApp'));

//import landingStyles from '../styles/LandingPage.module.css';



function sanitizeSlug(value) {
  return (
    String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '') || null
  );
}

function sanitizePath(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '');
}

function resolveInitialSlug(branding, internalPage) {
  const fromEnv = sanitizeSlug(process.env.REACT_APP_SHOP_SLUG);
  if (fromEnv) return fromEnv;

  const url = new URL(window.location.href);
  const querySlug =
    sanitizeSlug(url.searchParams.get('shop')) ||
    sanitizeSlug(url.searchParams.get('barberia'));
  if (querySlug) return querySlug;

  const bookingPath = sanitizePath(branding?.bookingPath);
  if (internalPage === 'booking' && branding?.shopSlug) {
    return sanitizeSlug(branding.shopSlug);
  }

  const [firstSegment] = url.pathname.replace(/^\/+|\/+$/g, '').split('/');
  if (sanitizePath(firstSegment) === bookingPath) {
    return sanitizeSlug(branding?.shopSlug);
  }
  return sanitizeSlug(firstSegment);
}

function resolveInternalPage(branding) {
  const url = new URL(window.location.href);
  const pathname = url.pathname.replace(/^\/+|\/+$/g, '');
  const segments = pathname ? pathname.split('/') : [];
  const bookingPath = sanitizePath(branding?.bookingPath);
  const brandOverride = url.searchParams.get('brand');

  if (segments[0] === 'panel') {
    return 'panel';
  }

  if ((branding?.isCustomDomain || brandOverride) && pathname === bookingPath) {
    return 'booking';
  }

  if (pathname === 'admin' || pathname === 'admin/subscriptions') {
    return 'subscription-admin';
  }

  if (pathname === 'monitoreo' || pathname === 'monitoring') {
    return 'monitoring';
  }

  if (pathname === 'admin/subscription-coupons') {
    return 'subscription-coupons';
  }

  if (pathname === 'planes' || pathname === 'suscripcion') {
    return 'subscription-checkout';
  }

  if (pathname === 'agregar-sucursal' || pathname === 'sumar-sucursal') {
    return 'add-branch';
  }

  if (
    pathname === 'registro' ||
    pathname === 'crear-cuenta' ||
    pathname === 'signup'
  ) {
    return 'register-account';
  }

  if (pathname === 'politica-de-privacidad' || pathname === 'privacy-policy') {
    return 'privacy-policy';
  }

  if (pathname === 'tutoriales' || pathname === 'tutorials') {
    return 'tutorials';
  }

  if (pathname === 'alianza') {
    return 'alianza';
  }

  // Link de referido (/r/CODIGO): va al registro con el cupón ya cargado.
  if (segments[0] === 'r' && segments[1]) {
    window.location.replace(`/registro?cupon=${encodeURIComponent(segments[1].toUpperCase())}`);
    return 'register-account';
  }

  if (
    pathname === 'soporte' ||
    pathname === 'contacto' ||
    pathname === 'support'
  ) {
    return 'support';
  }

  if (
    pathname === 'eliminacion-de-cuenta' ||
    pathname === 'eliminar-cuenta' ||
    pathname === 'account-deletion'
  ) {
    return 'account-deletion';
  }

  if (
    segments[0] === bookingPath ||
    segments[0] === 'admin' ||
    segments[0] === 'planes' ||
    segments[0] === 'suscripcion' ||
    segments[0] === 'registro' ||
    segments[0] === 'crear-cuenta' ||
    segments[0] === 'signup' ||
    segments[0] === 'soporte' ||
    segments[0] === 'contacto' ||
    segments[0] === 'support' ||
    segments[0] === 'politica-de-privacidad' ||
    segments[0] === 'privacy-policy' ||
    segments[0] === 'eliminacion-de-cuenta' ||
    segments[0] === 'eliminar-cuenta' ||
    segments[0] === 'account-deletion'
  ) {
    return 'not-found';
  }

  // Academia: área del alumno (/academia/...) y cursos de una barbería
  // (/<barbería>/academia y /<barbería>/academia/<curso>).
  if (segments[0] === 'academia') {
    return 'academy-student';
  }
  if (segments.length >= 2 && segments.length <= 3 && segments[1] === 'academia') {
    return 'academy-shop';
  }

  if (segments.length > 1) {
    return 'not-found';
  }

  return null;
}

function renderLandingByBranding(branding) {
  if (branding.landingDisabled) {
    return <SiteUnavailable />;
  }

  if (branding.customLanding === 'rich-barbershop') {
    return <RichBarbershopLanding branding={branding} />;
  }

  return <LandingPage branding={branding} />;
}


function AppRoutes() {
  const [branding] = useState(() => {
    const url = new URL(window.location.href);
    return resolveDomainBranding(
      window.location.hostname,
      url.searchParams.get('brand'),
    );
  });
  const [internalPage] = useState(() => resolveInternalPage(branding));
  const [shopSlug] = useState(() => resolveInitialSlug(branding, internalPage));
  const [missingShop, setMissingShop] = useState(false);

  // Seteamos el título de forma síncrona (antes del primer pintado) para que
  // no se vea el "BarberAppByCodex" estático del index.html en marcas propias.
  if (typeof document !== 'undefined') {
    document.title = branding?.siteName || 'BarberAppByCodex';
  }

  useEffect(() => {
    const pageTitle = branding?.siteName || 'BarberAppByCodex';
    document.title = pageTitle;

    const favicon = document.querySelector('link[rel="icon"]');
    const iconSrc = branding?.faviconSrc || branding?.logoSrc;
    if (favicon && iconSrc) {
      favicon.setAttribute('href', iconSrc);
    }

    const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (appleTouchIcon && iconSrc) {
      appleTouchIcon.setAttribute('href', iconSrc);
    }
  }, [branding]);

  if (internalPage === 'panel') {
    return <PanelApp />;
  }

  if (internalPage === 'subscription-admin') {
    return <SubscriptionAdmin />;
  }

  if (internalPage === 'monitoring') {
    return <MonitoringPanel />;
  }

  if (internalPage === 'subscription-coupons') {
    return <SubscriptionCouponsPage />;
  }

  if (internalPage === 'subscription-checkout') {
    return <SubscriptionCheckoutPage branding={branding} />;
  }

  if (internalPage === 'add-branch') {
    return <AddBranchPage branding={branding} />;
  }

  if (internalPage === 'register-account') {
    return <RegisterAccountPage branding={branding} />;
  }

  if (internalPage === 'privacy-policy') {
    return <PrivacyPolicyPage />;
  }

  if (internalPage === 'tutorials' && branding.showTutorials !== false) {
    return <TutorialesPage branding={branding} />;
  }

  if (internalPage === 'alianza') {
    return <AlianzaPage branding={branding} />;
  }

  if (internalPage === 'academy-student') {
    return <AcademyStudent />;
  }

  if (internalPage === 'academy-shop') {
    return <AcademyPublic />;
  }

  if (internalPage === 'support') {
    return <SupportPage />;
  }

  if (internalPage === 'account-deletion') {
    return <AccountDeletionPage />;
  }

  if (internalPage === 'not-found' || missingShop) {
    return <NotFoundPage />;
  }

  if (shopSlug) registerShopSlug(shopSlug);

  if (!shopSlug) {
    return renderLandingByBranding(branding);  // ← sin el main wrapper
  }

  return (
    <main className={styles.app}>
      <div className={styles.glow} aria-hidden="true" />
      <Suspense fallback={null}>
        <BookingForm
          shopSlug={shopSlug}
          branding={branding}
          onNotFound={() => setMissingShop(true)}
        />
      </Suspense>
    </main>
  );
}

// Mientras baja el código de la pantalla se muestra una caja del alto de la
// ventana, para que no haya saltos de layout al aparecer el contenido.
function App() {
  return (
    <Suspense fallback={<div style={{ minHeight: '100vh' }} aria-hidden="true" />}>
      <AppRoutes />
    </Suspense>
  );
}

export default App;
